import type { FC } from "react";
import { useCallback } from "react";

import { useDispatch } from "react-redux";

import { InfoTooltip } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import type { OnAddMultipleWebsites } from "app/pages/industry-analysis/market-builder/tabs/types";
import { allTrackers } from "app/services/track/track";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import { FadeWrapper } from "app/UtilitiesAndConstants/UtilitiesComponents/FadeWrapper";

import { useBuilderContext } from "../../builderContext";
import { EntitiesAutocomplete } from "../../components/entitiesAutocomplete/EntitiesAutocomplete";
import { ImportDomains } from "../../components/importDomains/ImportDomains";
import RecomendationsList from "../../components/recomendationsList/RecomendationsList";
import { useMarketBuilderServices } from "../../CustomHooks/useMarketBuilderServices";
import { ESelectDomainOptions } from "../../MarketBuilder.types";
import type { IDomain, IDomains } from "../../MarketBuilder.types";
import { getUniqueDomains, getValidDomains, parseDomainsFromClipboard } from "../../services/DomainsDataHandler";
import { MAX_DOMAINS_ALLOWED } from "../../services/ImportDomainsHandler";

import { EmptySuggestions } from "./EmptySuggestions";
import { LoadingSuggestions } from "./LoadingSuggestions";
import RefreshRecomendations from "./refreshRecomendations/RefreshRecomendations";
import {
    InputAndImportWrapper,
    RecomendationsContainer,
    RecomendationsListContainer,
    RecomendationsTitle,
    StyledFadeWrapper,
    WebsitesWrapper,
} from "./StyledComponents";

interface IWebsites {
    selectedEntities: IDomains;
    isLoading: boolean;
    suggestedDomains: IDomains;
    maxAllowedDomains: number;
    onAddWebsite: (domain: IDomain) => void;
    onAddMultipleWebsites: OnAddMultipleWebsites;
    getSuggestedDomains: (domains?: IDomains) => void;
    onBuildSegmentClick: VoidFunction;
    isSegmentsLoading: boolean;
}

const Websites: FC<IWebsites> = ({
    selectedEntities,
    isLoading,
    maxAllowedDomains,
    isSegmentsLoading,
    suggestedDomains,
    onAddMultipleWebsites,
    getSuggestedDomains,
    onAddWebsite,
    onBuildSegmentClick,
}) => {
    const services = useMarketBuilderServices();
    const showSuggestedBtn = selectedEntities.length > 0;
    const dispatch = useDispatch();
    const { showSegmentsDisclaimer, setShowSegmentsDisclaimer } = useBuilderContext();
    const MAX_SEGMENTS_IN_CATEGORY = swSettings.components.IndustryAnalysis.resources.MaxNumOfSegments;
    const onAutocompleteAddWebsite = (entity: IDomain) => {
        const eventName =
            entity.type === EAssetType.Segment
                ? `By websites/addSegment/${entity.domain} (${entity.segmentName}), ${entity.id}`
                : `By websites/addDomain/${entity.domain}`;

        allTrackers.trackEvent("Button", "click", eventName);

        entity.section = ESelectDomainOptions.WEBSITE;
        onAddWebsite(entity);
        if (!showSuggestedBtn) {
            getSuggestedDomains([entity]);
        }

        if (entity.type === EAssetType.Segment && !showSegmentsDisclaimer) {
            setShowSegmentsDisclaimer(true);
        }
    };
    const IS_ADD_DISABLED = selectedEntities.length >= MAX_DOMAINS_ALLOWED;

    const handleImportDomains = useCallback(
        (importedDomains: IDomain[]) => {
            const uniqueDomains = getUniqueDomains(importedDomains, selectedEntities);
            if (uniqueDomains.length <= 0) {
                dispatch(showSuccessToast(translate("custom.industry.create.domain.pasted.error.selected")));
                return;
            }

            allTrackers.trackEvent("Button", "click", `By websites/importDomains/${uniqueDomains.length}`);

            onAddMultipleWebsites(uniqueDomains, true, ESelectDomainOptions.WEBSITE);
            if (!showSuggestedBtn) {
                getSuggestedDomains(uniqueDomains);
            }

            const hasInvalidDomains = uniqueDomains.length < importedDomains.length;
            const addedDomainsMessage = hasInvalidDomains
                ? "custom.industry.create.domain.pasted.partial.valid"
                : "custom.industry.create.domains.added";

            dispatch(
                showSuccessToast(
                    translate(addedDomainsMessage, {
                        count: uniqueDomains.length,
                    }),
                ),
            );
        },
        [dispatch, getSuggestedDomains, onAddMultipleWebsites, selectedEntities, showSuggestedBtn],
    );

    /**
     * When the user pastes his clipboard (cmd+v), we want to automatically
     * Extract any domains on the clipboard and add them to the selected domains list.
     * Only domains that were not selected already will be added to the selection (a.k.a - unique domains).
     */
    const handlePasteDomains = useCallback(
        (event: ClipboardEvent) => {
            // Extract unique domains from the pasted list. (only domains that are not present in the user selection)
            const pastedDomains = parseDomainsFromClipboard(event);
            const uniqueDomains = getUniqueDomains(pastedDomains, selectedEntities);
            if (uniqueDomains.length <= 0) {
                dispatch(showSuccessToast(translate("custom.industry.create.domain.pasted.error.selected")));
                return;
            }

            // Extract valid domains from the pasted list. (only domains that are actually domains :D )
            const validDomains = getValidDomains(uniqueDomains.slice(0, maxAllowedDomains));
            if (validDomains.length <= 0) {
                dispatch(showSuccessToast(translate("custom.industry.create.domain.pasted.error.selected")));
                return;
            }

            allTrackers.trackEvent("Button", "click", `By websites/pasteDomain/${validDomains.length}`);

            onAddMultipleWebsites(validDomains, true, ESelectDomainOptions.WEBSITE);
            if (!showSuggestedBtn) {
                getSuggestedDomains(validDomains);
            }

            const hasInvalidDomains = validDomains.length < pastedDomains.length;
            const addedDomainsMessage = hasInvalidDomains
                ? "custom.industry.create.domain.pasted.partial.valid"
                : "custom.industry.create.domains.added";

            dispatch(
                showSuccessToast(
                    translate(addedDomainsMessage, {
                        count: validDomains.length,
                    }),
                ),
            );
        },
        [selectedEntities, maxAllowedDomains, onAddMultipleWebsites, showSuggestedBtn, dispatch, getSuggestedDomains],
    );

    const isSegmentsLimitReached = useCallback(() => {
        let segmentCount = 0;

        selectedEntities.forEach((domain) => {
            if (domain.type === EAssetType.Segment) {
                segmentCount++;
            }
        });

        if (MAX_SEGMENTS_IN_CATEGORY <= segmentCount) {
            return true;
        }
        return false;
    }, [MAX_SEGMENTS_IN_CATEGORY, selectedEntities]);

    const getRecommendationsElm = () => {
        return (
            <RecomendationsListContainer>
                <FadeWrapper
                    fadeHeight={"56px"}
                    ContentContainer={StyledFadeWrapper}
                >
                    <RecomendationsList
                        domainsList={suggestedDomains}
                        addToList={onAddWebsite}
                        isDisabled={IS_ADD_DISABLED}
                    />
                </FadeWrapper>
            </RecomendationsListContainer>
        );
    };

    return (
        <WebsitesWrapper data-automation="Websites">
            <InputAndImportWrapper>
                <EntitiesAutocomplete
                    excludeEntities={selectedEntities}
                    onAddEntity={onAutocompleteAddWebsite}
                    onPaste={handlePasteDomains}
                    isDisabled={IS_ADD_DISABLED}
                    isSegmentsLimitReached={isSegmentsLimitReached}
                    onAddMultipleWebsites={onAddMultipleWebsites}
                    onBuildSegmentClick={onBuildSegmentClick}
                    getSuggestedDomains={getSuggestedDomains}
                    showSuggestedBtn={showSuggestedBtn}
                    isSegmentsLoading={isSegmentsLoading}
                />
                <ImportDomains
                    services={services}
                    onImportDomains={handleImportDomains}
                    selectedDomains={selectedEntities}
                    isDisabled={IS_ADD_DISABLED}
                />
            </InputAndImportWrapper>
            <RecomendationsContainer>
                <RecomendationsTitle>
                    {translate("website.research.market.builder.title")}
                    <InfoTooltip info={translate("website.research.market.builder.tooltip.info")} />
                </RecomendationsTitle>
                {isLoading ? <LoadingSuggestions /> : null}
                {!isLoading ? suggestedDomains.length > 0 ? getRecommendationsElm() : <EmptySuggestions /> : null}
                {showSuggestedBtn ? <RefreshRecomendations onRefreshClick={getSuggestedDomains} /> : null}
            </RecomendationsContainer>
        </WebsitesWrapper>
    );
};

export default Websites;
